import {
  ReactElement,
  ChangeEvent,
  SyntheticEvent,
  useState,
  useCallback,
  useMemo,
} from 'react';
import { FormControl, MenuItem, FormHelperText } from '@mui/material';
import { find, throttle } from 'lodash';

import { Nullable } from '~globals/types';
import { CustomerPrediction } from '~hooks/useCustomersAutocomplete';
import { PlaceType } from '~hooks/usePlacesAutocomplete';
import { DEFAULT_UUIID_EMPTY } from '~constants/commons';

import {
  PointAddressSelectorProps,
  PointAddressSelectorOptionValue,
  PointAddressSelectorData,
} from './types';
import {
  PointAddressSelectorFieldSet,
  PointAddressSelectorSelectField,
  PointAddressSelectorPlacesAutocompleteField,
  PointAddressSelectorCustomersAutocompleteField,
} from './styles';
import PointAddressSelectorMenu from './Menu';

const PointAddressSelector = ({
  warehouses,
  defaultValues,
  onChange,
  onBlur,
  onReset,
  labels = {
    warehouse: 'Depósito',
    contact: 'Contacto',
    address: 'Dirección',
  },
  error = false,
  helperText,
  enabledEmptyOptionOnWarehouses = false,
}: PointAddressSelectorProps): ReactElement => {
  const [selectedOption, setSelectedOption] =
    useState<PointAddressSelectorOptionValue>('warehouse');

  const handleSelectedOption = useCallback(
    (currentOption: PointAddressSelectorOptionValue) => {
      setSelectedOption(currentOption);

      onReset?.(currentOption);

      onBlur?.();
    },
    [onReset, onBlur],
  );

  const handleChangeWarehouse = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target ?? event.currentTarget;

      let data: Nullable<PointAddressSelectorData> = null;

      if (value) {
        const currentWarehouseData = find(warehouses, { warehouseId: value });

        if (currentWarehouseData) {
          data = {
            id: currentWarehouseData.warehouseId,
            address: currentWarehouseData.address ?? '',
            latitude: currentWarehouseData.latitude,
            longitude: currentWarehouseData.longitude,
          };
        }
      }

      onChange?.({ type: 'warehouse', data });
    },
    [onChange, warehouses],
  );

  const handleChangeContact = useCallback(
    (
      _e: SyntheticEvent<Element, Event>,
      newValue: Nullable<CustomerPrediction>,
    ) => {
      let data: Nullable<PointAddressSelectorData> = null;

      if (newValue) {
        data = {
          id: newValue.customerId,
          address: newValue.address,
          latitude: newValue.latitude,
          longitude: newValue.longitude,
        };
      }

      onChange?.({ type: 'contact', data });
    },
    [onChange],
  );

  const getCoordAddress = useMemo(
    () =>
      throttle<google.maps.Geocoder['geocode']>((request, callback) => {
        new window.google.maps.Geocoder().geocode(request, callback);
      }, 200),
    [],
  );

  const handleChangeAddress = useCallback(
    (_e: SyntheticEvent<Element, Event>, newValue: Nullable<PlaceType>) => {
      if (newValue) {
        getCoordAddress({ placeId: newValue.place_id }, (results) => {
          if (results) {
            const { lat, lng } = results[0].geometry.location.toJSON();

            onChange?.({
              type: 'address',
              data: {
                id: DEFAULT_UUIID_EMPTY,
                address: newValue.description,
                latitude: lat,
                longitude: lng,
              },
            });
          }
        });
      } else {
        onChange?.({
          type: 'address',
          data: null,
        });
      }
    },
    [getCoordAddress, onChange],
  );

  return (
    <FormControl fullWidth error={error}>
      <PointAddressSelectorFieldSet error={error}>
        <PointAddressSelectorMenu
          selectedOption={selectedOption}
          onSelected={handleSelectedOption}
        />

        {selectedOption === 'warehouse' && (
          <PointAddressSelectorSelectField
            fullWidth
            label={labels.warehouse}
            select
            error={error}
            value={defaultValues?.warehouse ?? ''}
            onChange={handleChangeWarehouse}
            onBlur={onBlur}
          >
            {enabledEmptyOptionOnWarehouses && (
              <MenuItem value="">Sin especificar</MenuItem>
            )}

            {warehouses.map((warehouse) => (
              <MenuItem
                key={`point-addres-selector-warehouse-${warehouse.warehouseId}`}
                value={warehouse.warehouseId}
              >
                {warehouse.title}
              </MenuItem>
            ))}
          </PointAddressSelectorSelectField>
        )}

        {selectedOption === 'contact' && (
          <PointAddressSelectorCustomersAutocompleteField
            id="point-addres-selector-customers-autocomplete"
            label={labels.contact}
            onChange={handleChangeContact}
            onBlur={onBlur}
            error={error}
            fullWidth
            initialValueRequest={defaultValues?.contact}
          />
        )}

        {selectedOption === 'address' && (
          <PointAddressSelectorPlacesAutocompleteField
            id="point-addres-selector-places-autocomplete"
            label={labels.address}
            onChange={handleChangeAddress}
            onBlur={onBlur}
            error={error}
            fullWidth
            initialValueRequest={defaultValues?.address}
          />
        )}
      </PointAddressSelectorFieldSet>

      {helperText && (
        <FormHelperText variant="outlined" error={error}>
          {helperText}
        </FormHelperText>
      )}
    </FormControl>
  );
};

export default PointAddressSelector;
